import { useState } from "react";
import { AlertTriangle, CheckCircle2, ArrowRight, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

const beforeRequirement = {
  title: "User Story: Checkout Flow",
  content: `As a user, I want to be able to checkout quickly so that I can complete my purchase.

Acceptance Criteria:
- Users should be able to checkout
- Payment should work
- It should be fast`,
  issues: [
    "No definition of 'quickly' or 'fast'",
    "No error handling specified",
    "Missing payment method details",
    "No edge cases covered",
  ],
  score: 34,
};

const afterRequirement = {
  title: "User Story: Checkout Flow (Improved)",
  content: `As a returning customer with items in cart, I want to complete checkout in under 60 seconds using saved payment methods, so I can minimize friction and complete purchases on mobile.

Acceptance Criteria:
- Checkout completes in ≤3 steps for returning users
- Supports Stripe, PayPal, Apple Pay
- Shows real-time validation errors inline
- Handles failed payments with retry option
- Works offline-first with sync on reconnect

Assumptions:
- User is authenticated
- Cart validation happens before checkout
- Inventory is reserved for 15 minutes`,
  improvements: [
    "Explicit timing constraint (60s, 3 steps)",
    "Specific payment providers listed",
    "Error handling defined",
    "Edge cases addressed",
  ],
  score: 92,
};

export function BeforeAfterDemo() {
  const [activeTab, setActiveTab] = useState<"before" | "after">("before");

  return (
    <section className="py-14 md:py-20 relative overflow-hidden">
      {/* Background ambient */}
      <div className="absolute top-[20%] right-[10%] w-[300px] h-[300px] rounded-full bg-primary/[0.04] blur-[120px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Eye className="w-3.5 h-3.5" />
            See the difference
          </div>
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-3">
            See the difference Reqqy makes
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Watch a vague user story transform into clear, actionable requirements
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex p-1 rounded-full glass border border-border/50">
            <button
              onClick={() => setActiveTab("before")}
              className={cn(
                "px-5 py-2 rounded-full text-sm font-medium transition-all duration-300",
                activeTab === "before"
                  ? "bg-destructive/20 text-destructive shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Before Reqqy
            </button>
            <button
              onClick={() => setActiveTab("after")}
              className={cn(
                "px-5 py-2 rounded-full text-sm font-medium transition-all duration-300",
                activeTab === "after"
                  ? "gradient-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              After Reqqy
            </button>
          </div>
        </div>

        {/* Card */}
        <div className="max-w-3xl mx-auto">
          <div
            className={cn(
              "rounded-xl glass p-5 md:p-6 transition-all duration-500 border",
              activeTab === "before"
                ? "border-destructive/20"
                : "border-primary/20"
            )}
          >
            {/* Score Badge */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold">
                {activeTab === "before" ? beforeRequirement.title : afterRequirement.title}
              </h3>
              <div
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-semibold",
                  activeTab === "before"
                    ? "bg-destructive/20 text-destructive"
                    : "bg-primary/20 text-primary"
                )}
              >
                Clarity: {activeTab === "before" ? beforeRequirement.score : afterRequirement.score}%
              </div>
            </div>

            {/* Content */}
            <div className={cn(
              "rounded-lg p-4 mb-4 font-mono text-xs whitespace-pre-wrap border",
              activeTab === "before"
                ? "bg-destructive/[0.03] border-destructive/10"
                : "bg-primary/[0.03] border-primary/10"
            )}>
              {activeTab === "before" ? beforeRequirement.content : afterRequirement.content}
            </div>

            {/* Issues / Improvements */}
            <div>
              <h4 className="text-xs font-medium mb-2 flex items-center gap-1.5">
                {activeTab === "before" ? (
                  <>
                    <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                    Issues Found
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                    Improvements Made
                  </>
                )}
              </h4>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {(activeTab === "before" ? beforeRequirement.issues : afterRequirement.improvements).map(
                  (item, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex items-start gap-1.5 text-xs p-2.5 rounded-lg",
                        activeTab === "before"
                          ? "bg-destructive/[0.05]"
                          : "bg-primary/[0.05]"
                      )}
                    >
                      {activeTab === "before" ? (
                        <AlertTriangle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                      )}
                      {item}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {activeTab === "before" && (
            <div className="flex justify-center mt-5">
              <button
                onClick={() => setActiveTab("after")}
                className="group flex items-center gap-2 text-primary hover:text-primary/80 transition-all duration-300"
              >
                <span className="text-xs font-medium">See improvement</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
